'use client';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInputWithMask } from '@/components/form/ControlledInputWithMask/ControlledInputWithMask';
import { ControlledCheckbox } from '@/components/form/ControlledCheckbox/ControlledCheckbox';
import { ControlledInput } from '@/components/form/ControlledInput/ControlledInput';
import { SuccessPopup } from '@/components/common/SuccessPopup/SuccessPopup';
import { TextLink } from '@/components/ui/TextLink/TextLink';
import { Button } from '@/components';
import {
  type CallbackFormSchema,
  callbackFormSchema,
} from './callbackFormSchema';
import { submitForm } from '@/api/leads/leads';
import { personal } from '@/constants/docs';
import s from './callbackForm.module.scss';
import PhoneSvg from '@/assets/icons/Phone-Call--Streamline-Tabler.svg';
import MailSvg from '@/assets/icons/Mail-Heart--Streamline-Tabler.svg';
import { PhoneLink } from '@/components/ui/PhoneLink/PhoneLink';
import type { FooterInfoProps } from '@/components/footer/footerInfo/footerInfo.types';

const Modal = dynamic(() => import('@/components/layouts/Modal/Modal'), { ssr: false });

export const CallbackForm = ({ aboutData }: FooterInfoProps) => {
  const form = useForm<CallbackFormSchema>({
    resolver: zodResolver(callbackFormSchema),
    defaultValues: {
      name: '',
      phone: '',
      privacy_consent: false,
    },
  });

  const [successModal, setSuccessModal] = useState(false);

  const submit: SubmitHandler<CallbackFormSchema> = async (data) => {
    setSuccessModal(true);
    await submitForm({ ...data, phone: `+${data.phone}` });
    form.reset();
  };

  const closeSuccessModal = () => setSuccessModal(false);

  return (
    <>
      <FormProvider {...form} >
        <form className={s.form}>
          <div className={s.form__header}>
            <div className={s.form__title}>Обратный звонок</div>
            <div className={s.form__contacts}>
              <div className={s.form__contact}>
                <MailSvg />
                <a href={`mailto:${aboutData.email}`} className={s.email}>{aboutData.email}</a>
              </div>
              <div className={s.form__contact}>
                <PhoneSvg />
                <PhoneLink phone={aboutData.main_phone} codeIsFour />
              </div>
            </div>
          </div>
          <div className={s.form__hint}>Вам будет совершен обратный звонок, дождитесь ответа оператора.</div>
          <div className={s.inputsContainer}>
            <ControlledInput fieldName={'name'} placeholder={'Ваше имя'} />
            <ControlledInputWithMask
              fieldName={'phone'}
              maskOptions={[{ mask: '+{7}(000)000-00-00' }]}
              placeholder={'+7 (___) ___ -__-__'}
            />
          </div>
          <ControlledCheckbox className={s.form__checkbox} fieldName={'privacy_consent'}>
            <>Я принимаю <TextLink text={'Политику конфиденциальности и обработку персональных данных'} href={personal} target={'_blank'} /></>
          </ControlledCheckbox>
          <Button
            className={s.form__submitButton}
            onClick={form.handleSubmit(submit)}
            name="Отправить"
            theme="footer"
            size="block"
          />
        </form>
      </FormProvider>

      <Modal isOpen={successModal} onClose={closeSuccessModal}>
        <SuccessPopup onClose={closeSuccessModal} />
      </Modal>
    </>
  );
};